import React from "react";

import cx from "classnames";

import { IconDictionary } from "../Icon/types";

import styles from "./ToggleButton.scss";

export interface PopupButtonProps {
  /**
   * Whether the button is currently active.
   */
  active?: boolean;

  /**
   * Whether the button is disabled and non-interactive.
   */
  disabled?: boolean;

  /**
   * The icon to be displayed on the button.
   */
  icon: IconDictionary;

  /**
   * Function invoked when button is clicked.
   */
  onClick: (newActive: boolean) => void;

  /**
   * Label describing the button's action. Used to display a tooltip and apply an ARIA label.
   */
  label: string;

  /**
   * Optional test ID for testing purposes.
   */
  "data-testid"?: string;

  /**
   * Additional class names for the button.
   */
  className?: string;

  /**
   * The type of the button.
   */
  type?: "button" | "submit" | "reset" | undefined;
}

/**
 * A controlled button component with a persistent active/inactive state.
 */
export function PopupButton({
  active = false,
  disabled = false,
  label,
  onClick,
  className,
  "data-testid": testId,
  children,
  ...buttonProps
}: PopupButtonProps & Omit<React.HTMLProps<HTMLButtonElement>, "onClick">) {
  const buttonClassName = cx(
    styles.togglebutton,
    { [styles["selected"]]: active },
    className
  );

  return (
    <button
      title={label}
      aria-label={label}
      className={buttonClassName}
      onClick={() => onClick(!active)}
      disabled={disabled}
      data-testid={testId}
      aria-expanded={active}
      {...buttonProps}
    >
      {children}
    </button>
  );
}
